import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { Crop } from "react-image-crop";

export enum AspectRatio {
  FREE = "free",
  SQUARE = "1:1",
  LANDSCAPE_4_3 = "4:3",
  LANDSCAPE_16_9 = "16:9",
  PORTRAIT_3_4 = "3:4",
  PORTRAIT_9_16 = "9:16",
}

export enum OutputFormat {
  PNG = "png",
  JPG = "jpg",
  WEBP = "webp",
}

export interface ImageCropperState {
  // Image data
  originalImage: string | null;
  originalFileName: string | null;
  croppedImage: string | null;
  
  // Crop settings
  crop: Crop;
  aspectRatio: AspectRatio;
  outputFormat: OutputFormat;
  quality: number; // 0.1 to 1.0 for JPG/WEBP
  
  // UI state
  isImageLoaded: boolean;
  isCropping: boolean;
  error: string | null;
  
  // Dimensions
  originalWidth: number;
  originalHeight: number;
  croppedWidth: number;
  croppedHeight: number;
}

const initialState: ImageCropperState = {
  originalImage: null,
  originalFileName: null,
  croppedImage: null,
  
  crop: {
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  },
  aspectRatio: AspectRatio.FREE,
  outputFormat: OutputFormat.PNG,
  quality: 0.9,
  
  isImageLoaded: false,
  isCropping: false,
  error: null,
  
  originalWidth: 0,
  originalHeight: 0,
  croppedWidth: 0,
  croppedHeight: 0,
};

export const ImageCropperSlice = createSlice({
  name: "imageCropper",
  initialState,
  reducers: {
    imageUploaded: (state, action: PayloadAction<{ dataUrl: string; fileName: string; width: number; height: number }>) => {
      state.originalImage = action.payload.dataUrl;
      state.originalFileName = action.payload.fileName;
      state.originalWidth = action.payload.width;
      state.originalHeight = action.payload.height;
      state.isImageLoaded = true;
      state.croppedImage = null;
      state.error = null;
      
      // Reset crop to center 50% of image
      state.crop = {
        unit: '%',
        x: 25,
        y: 25,
        width: 50,
        height: 50,
      };
    },
    
    cropChanged: (state, action: PayloadAction<Crop>) => {
      state.crop = action.payload;
    },
    
    aspectRatioChanged: (state, action: PayloadAction<AspectRatio>) => {
      state.aspectRatio = action.payload;
      // Crop will be updated by the component when aspect ratio changes
    },
    
    outputFormatChanged: (state, action: PayloadAction<OutputFormat>) => {
      state.outputFormat = action.payload;
    },
    
    qualityChanged: (state, action: PayloadAction<number>) => {
      state.quality = Math.max(0.1, Math.min(1.0, action.payload));
    },
    
    cropStarted: (state) => {
      state.isCropping = true;
      state.error = null;
    },
    
    cropCompleted: (state, action: PayloadAction<{ dataUrl: string; width: number; height: number }>) => {
      state.croppedImage = action.payload.dataUrl;
      state.croppedWidth = action.payload.width;
      state.croppedHeight = action.payload.height;
      state.isCropping = false;
    },
    
    cropFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isCropping = false;
    },
    
    resetCropper: (state) => {
      return { ...initialState };
    },
    
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Helper function to get numeric aspect ratio value
function getAspectRatioValue(aspectRatio: AspectRatio): number | undefined {
  switch (aspectRatio) {
    case AspectRatio.SQUARE:
      return 1;
    case AspectRatio.LANDSCAPE_4_3:
      return 4 / 3;
    case AspectRatio.LANDSCAPE_16_9:
      return 16 / 9;
    case AspectRatio.PORTRAIT_3_4:
      return 3 / 4;
    case AspectRatio.PORTRAIT_9_16:
      return 9 / 16;
    default:
      return undefined;
  }
}

// Helper function to create a crop with proper aspect ratio
export function createCropWithAspectRatio(
  aspectRatio: AspectRatio,
  imageWidth: number,
  imageHeight: number,
  cropSize: number = 50 // percentage
): Crop {
  const ratioValue = getAspectRatioValue(aspectRatio);
  
  if (!ratioValue) {
    // Free aspect ratio
    return {
      unit: '%',
      x: (100 - cropSize) / 2,
      y: (100 - cropSize) / 2,
      width: cropSize,
      height: cropSize,
    };
  }
  
  // Calculate crop dimensions based on aspect ratio
  const imageAspect = imageWidth / imageHeight;
  let cropWidth, cropHeight;
  
  if (ratioValue > imageAspect) {
    // Crop is wider than image - fit to width
    cropWidth = cropSize;
    cropHeight = cropSize / ratioValue * imageAspect;
  } else {
    // Crop is taller than image - fit to height
    cropHeight = cropSize;
    cropWidth = cropSize * ratioValue / imageAspect;
  }
  
  return {
    unit: '%',
    x: (100 - cropWidth) / 2,
    y: (100 - cropHeight) / 2,
    width: cropWidth,
    height: cropHeight,
  };
}

export const {
  imageUploaded,
  cropChanged,
  aspectRatioChanged,
  outputFormatChanged,
  qualityChanged,
  cropStarted,
  cropCompleted,
  cropFailed,
  resetCropper,
  clearError,
} = ImageCropperSlice.actions;

export const selectState = (state: RootState) => state.imageCropper;

// Selectors for derived state
export const selectAspectRatioValue = (state: RootState) => {
  const { aspectRatio } = state.imageCropper;
  return getAspectRatioValue(aspectRatio);
};

// Utility functions
export function validateImageFile(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return "Please select a valid image file";
  }
  return null;
}

export function getMimeTypeAndQuality(outputFormat: OutputFormat, quality: number): { mimeType: string; quality: number | undefined } {
  switch (outputFormat) {
    case OutputFormat.JPG:
      return { mimeType: "image/jpeg", quality };
    case OutputFormat.WEBP:
      return { mimeType: "image/webp", quality };
    case OutputFormat.PNG:
    default:
      return { mimeType: "image/png", quality: undefined };
  }
}

export function generateFileName(originalFileName: string | null, outputFormat: OutputFormat): string {
  if (originalFileName) {
    const baseName = originalFileName.split('.')[0];
    return `${baseName}_cropped.${outputFormat}`;
  }
  return `cropped_image.${outputFormat}`;
}

export function convertCropToPixels(crop: Crop, naturalWidth: number, naturalHeight: number, displayWidth?: number, displayHeight?: number): { x: number; y: number; width: number; height: number } {
  if (crop.unit === "%") {
    return {
      x: Math.round((crop.x / 100) * naturalWidth),
      y: Math.round((crop.y / 100) * naturalHeight),
      width: Math.round((crop.width / 100) * naturalWidth),
      height: Math.round((crop.height / 100) * naturalHeight),
    };
  } else {
    // For pixel crops, we need to scale from display size to natural size
    if (displayWidth && displayHeight) {
      const scaleX = naturalWidth / displayWidth;
      const scaleY = naturalHeight / displayHeight;
      return {
        x: Math.round(crop.x * scaleX),
        y: Math.round(crop.y * scaleY),
        width: Math.round(crop.width * scaleX),
        height: Math.round(crop.height * scaleY),
      };
    } else {
      // Fallback: assume crop is already in natural size
      return {
        x: Math.round(crop.x),
        y: Math.round(crop.y),
        width: Math.round(crop.width),
        height: Math.round(crop.height),
      };
    }
  }
}

export default ImageCropperSlice.reducer;
